// ============================================================
// V2 platform — tenant theme injection.
//
// R5 (2026-04-24): per-tenant visual branding. Reads the tenant
// config doc's `theme` field and injects CSS variable overrides
// onto :root via documentElement.style.setProperty.
//
// Why setProperty (not an injected <style>): targeted (only the
// vars we override), live-updatable (Settings color picker re-
// applies on every input event), and zero-FOUC if called before
// renderApp.
//
// Branding-relevant CSS variables (per-tenant):
//   --accent, --accent2, --accent-tint
//   --font (font family)
//
// Semantic / structural variables stay UNIVERSAL:
//   --green, --yellow, --red, --orange, --purple, --cyan
//   --text, --text2, --text3, --border, --border-subtle
//   --bg, --sidebar, --card, --card2, --surface (kept universal in R5
//     — too entangled with semantic palette; tenant-customizable
//     surface tones are a future "theme variant" feature.)
//
// Owner: principal-architect (variable taxonomy) + ui-designer
// (color manipulation) + firebase-expert (boot wiring).
// Debate captured in DESIGN_LOG.md "R5 — Theming + branding".
// ============================================================

// ─────────────────────────────────────────────────────────────
// Color helpers — convert hex → HSL → adjust lightness → hex.
// Pure functions, no DOM. Used to derive --accent2 (darker) and
// --accent-tint (lighter) from the single primaryColor input so
// admins only need to pick ONE color, not three.
// ─────────────────────────────────────────────────────────────

function _hexToRgb(hex) {
  if (!hex) return null;
  const s = String(hex).trim().replace(/^#/, '');
  if (!/^[0-9a-f]{3}$|^[0-9a-f]{6}$/i.test(s)) return null;
  const full = s.length === 3 ? s.split('').map(c => c + c).join('') : s;
  const n = parseInt(full, 16);
  return { r: (n >> 16) & 0xff, g: (n >> 8) & 0xff, b: n & 0xff };
}

function _rgbToHex({ r, g, b }) {
  const h = (v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0');
  return '#' + h(r) + h(g) + h(b);
}

function _rgbToHsl({ r, g, b }) {
  const R = r / 255, G = g / 255, B = b / 255;
  const max = Math.max(R, G, B), min = Math.min(R, G, B);
  const l = (max + min) / 2;
  let h, s;
  if (max === min) { h = s = 0; }
  else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case R: h = (G - B) / d + (G < B ? 6 : 0); break;
      case G: h = (B - R) / d + 2; break;
      case B: h = (R - G) / d + 4; break;
    }
    h *= 60;
  }
  return { h, s, l };
}

function _hslToRgb({ h, s, l }) {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let r1, g1, b1;
  if      (h < 60)  { r1 = c; g1 = x; b1 = 0; }
  else if (h < 120) { r1 = x; g1 = c; b1 = 0; }
  else if (h < 180) { r1 = 0; g1 = c; b1 = x; }
  else if (h < 240) { r1 = 0; g1 = x; b1 = c; }
  else if (h < 300) { r1 = x; g1 = 0; b1 = c; }
  else              { r1 = c; g1 = 0; b1 = x; }
  return { r: (r1 + m) * 255, g: (g1 + m) * 255, b: (b1 + m) * 255 };
}

/** Darken a hex color by `percent` lightness points (e.g. 15 = -15%L). */
export function darken(hex, percent) {
  const rgb = _hexToRgb(hex); if (!rgb) return hex;
  const hsl = _rgbToHsl(rgb);
  hsl.l = Math.max(0, Math.min(1, hsl.l - percent / 100));
  return _rgbToHex(_hslToRgb(hsl));
}

/** Lighten a hex color by `percent` lightness points. */
export function lighten(hex, percent) {
  const rgb = _hexToRgb(hex); if (!rgb) return hex;
  const hsl = _rgbToHsl(rgb);
  hsl.l = Math.max(0, Math.min(1, hsl.l + percent / 100));
  return _rgbToHex(_hslToRgb(hsl));
}

/** True if the given hex is a valid 3- or 6-digit color. */
export function isValidHex(hex) {
  return _hexToRgb(hex) !== null;
}

// ─────────────────────────────────────────────────────────────
// Apply / reset — the two functions main.js + Settings call.
// ─────────────────────────────────────────────────────────────

const _OVERRIDABLE_VARS = ['--accent', '--accent2', '--accent-tint', '--font'];

/**
 * Apply a tenant theme by overriding CSS variables on :root.
 * Idempotent — calling again with a different theme replaces the
 * overrides cleanly.
 *
 * Accepts the `theme` object from the tenant config doc:
 *   { brandName, tagline, primaryColor, accentColor, logoPath,
 *     logoUrl, faviconPath, faviconUrl, font }
 *
 * Falsy/missing fields are SKIPPED — the CSS default (CCY beige-
 * coffee) shows through. This is the bootstrap path for a tenant
 * doc that has never been customized.
 */
export function applyTenantTheme(theme) {
  if (!theme || typeof theme !== 'object') {
    resetTenantTheme();
    return;
  }
  const root = document.documentElement;

  if (theme.primaryColor && isValidHex(theme.primaryColor)) {
    root.style.setProperty('--accent',       theme.primaryColor);
    root.style.setProperty('--accent2',      darken(theme.primaryColor, 15));
    root.style.setProperty('--accent-tint',  lighten(theme.primaryColor, 35));
  } else {
    // Unset to fall back to CSS default.
    root.style.removeProperty('--accent');
    root.style.removeProperty('--accent2');
    root.style.removeProperty('--accent-tint');
  }

  // accentColor reserved — currently unused in the CSS. Kept in the
  // schema for forthcoming "secondary action" surfaces (e.g., a
  // contrasting CTA for paid features). Skipping the setProperty
  // here is intentional; the field is recorded but visually inert.

  if (theme.font && typeof theme.font === 'string' && theme.font.trim()) {
    root.style.setProperty('--font', theme.font);
  } else {
    root.style.removeProperty('--font');
  }

  // Update document.title + favicon if provided (these are not CSS).
  if (theme.brandName) {
    document.title = String(theme.brandName) + ' · ERP';
  }
  if (theme.faviconUrl) {
    _setFaviconHref(theme.faviconUrl);
  }
}

/** Remove all tenant overrides — CSS defaults take over. */
export function resetTenantTheme() {
  const root = document.documentElement;
  for (const v of _OVERRIDABLE_VARS) root.style.removeProperty(v);
}

/**
 * Live-preview a partial theme without writing it. Used by the
 * Settings color picker on every input event so admins see the
 * change before clicking Save. Cancelling the modal calls
 * applyTenantTheme(committedTheme) to revert.
 */
export function previewTheme(partialTheme) {
  applyTenantTheme(partialTheme);
}

// ─────────────────────────────────────────────────────────────
// Favicon swap — replaces the <link rel="icon"> href in <head>.
// If no link tag exists yet, creates one.
// ─────────────────────────────────────────────────────────────

function _setFaviconHref(url) {
  if (!url) return;
  let link = document.querySelector('link[rel="icon"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = url;
}
